/**
 * ViewLevelSelector Component - Enhanced with Selectors
 * TASK-207: Portfolio View Hierarchy Navigation with Client/Portfolio Selection
 *
 * Three-level selector for portfolio view hierarchy:
 * - My Book: Advisor's entire book (all clients)
 * - Client Level: Single client's multi-portfolio view + Client Selector
 * - Portfolio Level: Single portfolio deep dive + Portfolio Selector
 */

import React, { useState, useRef, useEffect } from 'react';
import { BookOpen, Users, BarChart3, ChevronDown, Check } from 'lucide-react';
import { ViewLevel } from '../../../types/portfolio-views';

export interface ClientOption {
  id: string;
  name: string;
  totalAuM?: number;
}

export interface PortfolioOption {
  id: string;
  name: string;
  clientId: string;
  value?: number;
}

interface ViewLevelSelectorProps {
  currentLevel: ViewLevel;
  onLevelChange: (level: ViewLevel, id?: string) => void;
  disabled?: boolean;
  clientId?: string;
  portfolioId?: string;
  // Available options for selection
  clients?: ClientOption[];
  portfolios?: PortfolioOption[];
}

const ViewLevelSelector: React.FC<ViewLevelSelectorProps> = ({
  currentLevel,
  onLevelChange,
  disabled = false,
  clientId,
  portfolioId,
  clients = [],
  portfolios = [],
}) => {
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [showPortfolioDropdown, setShowPortfolioDropdown] = useState(false);

  const clientDropdownRef = useRef<HTMLDivElement>(null);
  const portfolioDropdownRef = useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (clientDropdownRef.current && !clientDropdownRef.current.contains(event.target as Node)) {
        setShowClientDropdown(false);
      }
      if (portfolioDropdownRef.current && !portfolioDropdownRef.current.contains(event.target as Node)) {
        setShowPortfolioDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const levels = [
    {
      value: 'my-book' as const,
      label: 'My Book',
      icon: BookOpen,
      description: 'All clients overview',
      enabled: true,
      hasSelector: false,
    },
    {
      value: 'client' as const,
      label: 'Client Level',
      icon: Users,
      description: 'Multi-portfolio view',
      enabled: clients.length > 0,
      hasSelector: true,
    },
    {
      value: 'portfolio' as const,
      label: 'Portfolio Level',
      icon: BarChart3,
      description: 'Single portfolio deep dive',
      enabled: portfolios.length > 0,
      hasSelector: true,
    },
  ];

  const handleLevelClick = (level: ViewLevel, hasSelector: boolean) => {
    if (disabled) return;

    // Check if level is enabled
    const levelConfig = levels.find(l => l.value === level);
    if (!levelConfig?.enabled) {
      console.warn(`Cannot navigate to ${level}: no options available`);
      return;
    }

    // If current level and has selector, toggle dropdown
    if (currentLevel === level && hasSelector) {
      if (level === 'client') {
        setShowClientDropdown(!showClientDropdown);
        setShowPortfolioDropdown(false);
      } else if (level === 'portfolio') {
        setShowPortfolioDropdown(!showPortfolioDropdown);
        setShowClientDropdown(false);
      }
      return;
    }

    // If clicking different level
    if (level === 'my-book') {
      // Go directly to My Book
      onLevelChange('my-book');
      setShowClientDropdown(false);
      setShowPortfolioDropdown(false);
    } else if (level === 'client') {
      // TASK-210: If we have a clientId, navigate to that client's view directly
      // Otherwise show the selector
      if (clientId) {
        onLevelChange('client', clientId);
        setShowClientDropdown(false);
        setShowPortfolioDropdown(false);
      } else {
        // Show client selector
        setShowClientDropdown(true);
        setShowPortfolioDropdown(false);
      }
    } else if (level === 'portfolio') {
      // Show portfolio selector
      setShowPortfolioDropdown(true);
      setShowClientDropdown(false);
    }
  };

  const handleClientSelect = (selectedClientId: string) => {
    onLevelChange('client', selectedClientId);
    setShowClientDropdown(false);
  };

  const handlePortfolioSelect = (selectedPortfolioId: string) => {
    onLevelChange('portfolio', selectedPortfolioId);
    setShowPortfolioDropdown(false);
  };

  const getCurrentClientName = () => {
    const client = clients.find(c => c.id === clientId);
    return client?.name || 'Select Client';
  };

  const getCurrentPortfolioName = () => {
    const portfolio = portfolios.find(p => p.id === portfolioId);
    return portfolio?.name || 'Select Portfolio';
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        View Level
      </label>
      <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1 relative">
        {levels.map((level) => {
          const isSelected = currentLevel === level.value;
          const Icon = level.icon;
          const isEnabled = level.enabled && !disabled;

          return (
            <div key={level.value} className="relative" ref={
              level.value === 'client' ? clientDropdownRef :
              level.value === 'portfolio' ? portfolioDropdownRef :
              undefined
            }>
              <button
                type="button"
                onClick={() => handleLevelClick(level.value, level.hasSelector)}
                disabled={!isEnabled}
                className={`
                  inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium
                  transition-colors duration-200
                  ${
                    isSelected
                      ? 'bg-blue-600 text-white shadow-sm'
                      : isEnabled
                      ? 'text-gray-700 hover:bg-gray-50'
                      : 'text-gray-400 cursor-not-allowed'
                  }
                `}
                title={isEnabled ? level.description : `${level.description} (unavailable)`}
              >
                <Icon className="w-4 h-4" />
                <span className="whitespace-nowrap">
                  {level.value === 'client' && clientId
                    ? getCurrentClientName()
                    : level.value === 'portfolio' && isSelected && portfolioId
                    ? getCurrentPortfolioName()
                    : level.label}
                </span>
                {level.hasSelector && isEnabled && (
                  <ChevronDown className="w-3 h-3" />
                )}
              </button>

              {/* Client Dropdown */}
              {level.value === 'client' && showClientDropdown && isEnabled && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <p className="text-xs font-medium text-gray-700">Select Client</p>
                  </div>
                  <div className="p-2">
                    {clients.map((client) => (
                      <button
                        key={client.id}
                        onClick={() => handleClientSelect(client.id)}
                        className={`w-full text-left px-3 py-3 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-between ${
                          clientId === client.id ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <div className="text-sm font-medium text-gray-900">{client.name}</div>
                            {client.totalAuM !== undefined && (
                              <div className="text-sm font-bold text-[#002B45] ml-4">
                                CHF {(client.totalAuM / 1000000).toFixed(2)}M
                              </div>
                            )}
                          </div>
                          {client.totalAuM !== undefined && (
                            <div className="text-xs text-gray-500">
                              Total AuM: {new Intl.NumberFormat('de-CH', { style: 'currency', currency: 'CHF', minimumFractionDigits: 0 }).format(client.totalAuM)}
                            </div>
                          )}
                        </div>
                        {clientId === client.id && (
                          <Check className="w-4 h-4 text-blue-600 ml-2 flex-shrink-0" />
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Portfolio Dropdown */}
              {level.value === 'portfolio' && showPortfolioDropdown && isEnabled && (
                <div className="absolute top-full left-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50 max-h-96 overflow-y-auto">
                  <div className="p-2 border-b border-gray-200 bg-gray-50">
                    <p className="text-xs font-medium text-gray-700">Select Portfolio</p>
                  </div>
                  <div className="p-2">
                    {portfolios
                      .filter(portfolio => {
                        // TASK-210: If clientId is set, only show portfolios for that client
                        if (clientId) {
                          return portfolio.clientId === clientId;
                        }
                        // Otherwise show all portfolios
                        return true;
                      })
                      .map((portfolio) => {
                      // Find client name for this portfolio
                      const client = clients.find(c => c.id === portfolio.clientId);
                      const clientName = client?.name || 'Unknown Client';

                      return (
                        <button
                          key={portfolio.id}
                          onClick={() => handlePortfolioSelect(portfolio.id)}
                          className={`w-full text-left px-3 py-3 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-between ${
                            portfolioId === portfolio.id ? 'bg-blue-50' : ''
                          }`}
                        >
                          <div className="flex-1">
                            <div className="flex items-center justify-between mb-1">
                              <div className="text-sm font-medium text-gray-900">{portfolio.name}</div>
                              {portfolio.value !== undefined && (
                                <div className="text-sm font-bold text-[#002B45] ml-4">
                                  CHF {(portfolio.value / 1000000).toFixed(2)}M
                                </div>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">
                              Client: {clientName}
                            </div>
                          </div>
                          {portfolioId === portfolio.id && (
                            <Check className="w-4 h-4 text-blue-600 ml-2 flex-shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Current Selection Info */}
      <p className="text-xs text-gray-500">
        {currentLevel === 'my-book' && 'Viewing all clients in your book'}
        {currentLevel === 'client' && `Viewing: ${getCurrentClientName()}`}
        {currentLevel === 'portfolio' && (
          <>
            {clientId && `Client: ${getCurrentClientName()} • `}
            Portfolio: {getCurrentPortfolioName()}
          </>
        )}
      </p>
    </div>
  );
};

export default ViewLevelSelector;
